import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  SG_GET_PUBLIC_UPLOADS,
  SG_DELETE_PUBLIC_UPLOAD,
  UPDATE_PUBLIC_UPLOAD_PAGE_NUMBER,
} from "constants/actions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import { Search, Plus, Eye, Edit, Trash2, FileText, Inbox, Loader2, Info, Copy, Check } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "all", label: "All Categories" },
  { value: "pdf_template", label: "PDF Template" },
  { value: "logo", label: "Logo" },
  { value: "css", label: "CSS" },
  { value: "js", label: "JavaScript" },
  { value: "json", label: "JSON" },
  { value: "branding", label: "Branding" },
  { value: "document", label: "Document" },
  { value: "image", label: "Image" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

export default function UploadList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { publicUploads, pageNumber, loading } = useSelector(
    (state) => state.public_upload
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const makeSpaceSafeUrl = (url) => (typeof url === "string" ? encodeURI(url) : url);

  useEffect(() => {
    loadUploads();
  }, [pageNumber, categoryFilter, statusFilter]);

  const loadUploads = () => {
    const params = {
      page: pageNumber,
      search: searchTerm,
    };

    if (categoryFilter && categoryFilter !== "all") {
      params.category = categoryFilter;
    }

    if (statusFilter && statusFilter !== "all") {
      params.status = statusFilter;
    }

    dispatch({ type: SG_GET_PUBLIC_UPLOADS, payload: params });
  };

  const handleSearch = () => {
    dispatch({ type: UPDATE_PUBLIC_UPLOAD_PAGE_NUMBER, payload: 1 });
    loadUploads();
  };

  const handlePageChange = (page) => {
    dispatch({ type: UPDATE_PUBLIC_UPLOAD_PAGE_NUMBER, payload: page });
  };

  const handleDelete = () => {
    dispatch({ type: SG_DELETE_PUBLIC_UPLOAD, payload: { id: deleteId } });
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const copyToClipboard = async (url, id) => {
    try {
      const safeUrl = makeSpaceSafeUrl(url);
      await navigator.clipboard.writeText(safeUrl);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const getStatusColor = (status) => {
    return status === 1 ? "default" : "secondary";
  };

  const totalPages = publicUploads?.count
    ? Math.ceil(publicUploads.count / 20)
    : 1;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <FileText className="h-6 w-6" />
          Public Uploads
              </CardTitle>
              <CardDescription>
            Manage uploaded files and assets
              </CardDescription>
            </div>
            <Button onClick={() => navigate("/app/public-uploads/upload/new")}>
              <Plus className="h-4 w-4 mr-2" />
              Upload New File
            </Button>
          </div>
        </CardHeader>
        <CardContent>
      {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
        <Input
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="w-full"
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Search
        </Button>
          </div>

      {/* Results Count */}
      {publicUploads?.count > 0 && (
            <Alert className="mb-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
          Found {publicUploads.count} upload{publicUploads.count !== 1 ? "s" : ""}
              </AlertDescription>
            </Alert>
      )}

      {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>File Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
          {loading ? (
                  <TableRow>
                    <TableCell colSpan="8" className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
                    </TableCell>
                  </TableRow>
          ) : publicUploads?.results?.length > 0 ? (
            publicUploads.results.map((upload) => (
                    <TableRow key={upload.id}>
                      <TableCell className="font-medium">{upload.id}</TableCell>
                      <TableCell>
                        <button
                          onClick={() => copyToClipboard(upload.file_url, upload.id)}
                          className="flex items-center gap-2 hover:underline text-primary cursor-pointer"
                        >
                          <FileText className="h-4 w-4" />
                          <span>{upload.name}</span>
                          {copiedId === upload.id ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <Copy className="h-3 w-3 opacity-50 hover:opacity-100" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{upload.category_text}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {upload.file_type || upload.mime_type}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatFileSize(upload.file_size)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(upload.status)}>
                    {upload.status_text}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                  {format(new Date(upload.created_at), "MMM dd, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                    <Button
                            variant="ghost"
                            size="icon"
                      onClick={() =>
                        navigate(`/app/public-uploads/upload/${upload.id}`)
                      }
                    >
                            <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                            variant="ghost"
                            size="icon"
                      onClick={() =>
                        navigate(`/app/public-uploads/upload/edit/${upload.id}`)
                      }
                    >
                            <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                            variant="ghost"
                            size="icon"
                      onClick={() => confirmDelete(upload.id)}
                    >
                            <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                        </div>
                      </TableCell>
                    </TableRow>
            ))
          ) : (
                  <TableRow>
                    <TableCell colSpan="8" className="text-center py-12">
                      <Inbox className="h-12 w-12 mx-auto text-muted-foreground/50" />
                      <h3 className="mt-4 text-lg font-medium text-muted-foreground">
                  No uploads found
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        Try adjusting your filters or upload a new file
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
        {totalPages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => pageNumber > 1 && handlePageChange(pageNumber - 1)}
                      className={pageNumber === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pageNumber <= 3) {
                      pageNum = i + 1;
                    } else if (pageNumber >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = pageNumber - 2 + i;
                    }
                    
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          onClick={() => handlePageChange(pageNum)}
                          isActive={pageNumber === pageNum}
                          className="cursor-pointer"
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  
                  {totalPages > 5 && pageNumber < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                  
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => pageNumber < totalPages && handlePageChange(pageNumber + 1)}
                      className={pageNumber === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Upload</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this upload? This will also delete the file from S3 and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
